import { Box, keyframes, styled } from '@mui/material';
import { BRAND_NAVY_DEEP, BRAND_NAVY_LIGHT } from 'src/helpers/uiTokens';
import {
  GOLD_DEEP,
  GOLD_INK,
  GOLD_LIGHT,
  GOLD_TEXT,
} from 'src/components/careerProPitch/sheet.styles';

export type CoreMood = 'idle' | 'hover' | 'thinking' | 'speaking';

/* The assistant wears the platform's gold -- the same golds as the Pro pass --
 * on white. Fixed in both themes: the core is a light source, and gold that
 * shifts isn't gold. Text sitting on gold uses GOLD_INK; gold text on white
 * uses GOLD_TEXT, the only shades that stay legible. */
export const GOLD_SOFT = GOLD_LIGHT;
export const GOLD = '#F5B300';
export const GOLD_RICH = GOLD_DEEP;
export const CHAMPAGNE = '#FFF3C4';
export const BRONZE = '#5C3D00';
export { GOLD_INK, GOLD_TEXT };
export const GOLD_GRADIENT = `linear-gradient(120deg, ${GOLD_SOFT} 0%, ${GOLD} 45%, ${GOLD_RICH} 100%)`;
/** Gold that reads as text on white (headings, links). */
/** Depth comes from neutral shadows; gold is an accent, never a glow. */
export const SHADOW_SM = '0 2px 6px -2px rgba(17, 14, 6, 0.14)';
export const SHADOW_MD = '0 8px 22px -12px rgba(17, 14, 6, 0.28)';
export const SHADOW_LG =
  '0 24px 60px -24px rgba(17, 14, 6, 0.38), 0 8px 20px -12px rgba(17, 14, 6, 0.18)';
export const GOLD_TEXT_GRADIENT = `linear-gradient(120deg, ${GOLD_RICH} 0%, ${GOLD_TEXT} 100%)`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const breathe = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); }
  50% { transform: scale(1.07) rotate(0deg); }
`;
/* Every few seconds the star turns a quarter and flares -- enough movement in
 * the corner of the eye to be noticed, then still again. */
const flourish = keyframes`
  0%, 78%, 100% { transform: scale(1) rotate(0deg); filter: brightness(1); }
  84% { transform: scale(1.18) rotate(45deg); filter: brightness(1.35); }
  92% { transform: scale(.96) rotate(90deg); filter: brightness(1); }
`;
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.12); }
`;
const twinkle = keyframes`
  0%, 100% { transform: scale(.4) rotate(0deg); opacity: .2; }
  45% { transform: scale(1) rotate(45deg); opacity: 1; }
  70% { transform: scale(.7) rotate(90deg); opacity: .7; }
`;
const sweep = keyframes`
  0%, 70% { transform: translateX(-140%) rotate(25deg); }
  100% { transform: translateX(260%) rotate(25deg); }
`;

const RIM_SECONDS: Record<CoreMood, number> = {
  idle: 9,
  hover: 4,
  thinking: 1.6,
  speaking: 2.6,
};

interface CoreProps {
  size: number;
  mood: CoreMood;
}

const coreProps = (prop: PropertyKey) => prop !== 'size' && prop !== 'mood';

export const CoreRoot = styled(Box, {
  shouldForwardProp: coreProps,
})<CoreProps>(({ size }) => ({
  position: 'relative',
  width: size,
  height: size,
  flexShrink: 0,
  borderRadius: '50%',
  isolation: 'isolate',
  '& *': { pointerEvents: 'none' },
  '@media (prefers-reduced-motion: reduce)': {
    '& *': { animation: 'none !important' },
  },
}));

/* A thin gold rim with one bright arc running round it. */
export const CoreRim = styled(Box, {
  shouldForwardProp: coreProps,
})<CoreProps>(({ size, mood }) => ({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  padding: Math.max(1.5, size * 0.035),
  background: `conic-gradient(from 0deg, ${GOLD_RICH}, ${GOLD_SOFT} 18%, ${CHAMPAGNE} 24%, ${GOLD} 34%, ${GOLD_RICH} 60%, ${BRONZE} 82%, ${GOLD_RICH})`,
  WebkitMask:
    'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
  WebkitMaskComposite: 'xor',
  maskComposite: 'exclude',
  animation: `${spin} ${RIM_SECONDS[mood]}s linear infinite`,
  transition: 'animation-duration .4s',
}));

/* The brand navy, lit from the top left, with a faint gold warmth behind
 * the star. */
export const CoreDisc = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: '5%',
  borderRadius: '50%',
  overflow: 'hidden',
  background: `radial-gradient(circle at 50% 55%, ${GOLD}2e 0%, transparent 52%), radial-gradient(circle at 30% 22%, #2d5f93 0%, ${BRAND_NAVY_LIGHT} 48%, ${BRAND_NAVY_DEEP} 100%)`,
  boxShadow: 'inset 0 1px 1px #ffffff40, inset 0 -3px 8px #00000059',
  ...theme.applyStyles('dark', {
    boxShadow: 'inset 0 1px 1px #ffffff26, inset 0 -3px 8px #00000080',
  }),
}));

/* A soft band of light that crosses the face now and then. */
export const CoreSheen = styled(Box)({
  position: 'absolute',
  top: '-20%',
  bottom: '-20%',
  left: 0,
  width: '35%',
  background: 'linear-gradient(90deg, transparent, #ffffff2e, transparent)',
  animation: `${sweep} 5.5s ease-in-out infinite`,
  animationDelay: '1.5s',
});

export const CoreStar = styled('svg', {
  shouldForwardProp: (prop) => prop !== 'mood',
})<{ mood: CoreMood }>(({ mood }) => ({
  position: 'absolute',
  left: '24%',
  top: '24%',
  width: '52%',
  height: '52%',
  overflow: 'visible',
  filter: `drop-shadow(0 1px 2px ${BRONZE}99)`,
  animation:
    mood === 'thinking'
      ? `${spin} 2.2s cubic-bezier(.6,0,.4,1) infinite`
      : mood === 'speaking'
        ? `${pulse} .9s ease-in-out infinite`
        : mood === 'hover'
          ? `${breathe} 1.6s ease-in-out infinite`
          : `${flourish} 7s ease-in-out infinite`,
}));

export const CoreTwinkle = styled('svg', {
  shouldForwardProp: (prop) => prop !== 'delay' && prop !== 'scale',
})<{ delay: number; scale: number }>(({ delay, scale }) => ({
  position: 'absolute',
  width: `${scale}%`,
  height: `${scale}%`,
  animation: `${twinkle} 2.8s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));
